/**
 * Popup / overlay handlers (Cluster F items 47, 48, 49, 51).
 *
 * Cross-product demos repeatedly hit the same kinds of overlays:
 *   - Cookie / GDPR consent banners (OneTrust, Cookiebot, Termly, …)
 *   - Onboarding product tours (Intro.js, Driver.js, Userflow, Pendo,
 *     WalkMe, Appcues, Shepherd)
 *   - Custom modals (fixed/absolute divs without role="dialog")
 *   - Toast / live-region notifications (success/error states)
 *
 * Each is a category — not just a single library — so we use signature-based
 * detection rather than per-library hooks. The agent calls these helpers
 * from `iframe-agent` after navigation and after each action that might
 * surface an overlay.
 */

/**
 * Item 48 — cookie-banner / consent dismiss. Returns true if a banner was
 * detected and an "accept" / "got it" click was dispatched.
 *
 * Heuristic: walk known per-library selectors first (cheap), fall back to
 * a generic "fixed-position container with accept button + cookie text"
 * scan. Avoids false positives by requiring BOTH a banner-like layout AND
 * cookie-related text content.
 */
const COOKIE_BANNER_SELECTORS = [
  // OneTrust
  '#onetrust-banner-sdk',
  '#onetrust-accept-btn-handler',
  // Cookiebot
  '#CybotCookiebotDialog',
  '#CybotCookiebotDialogBodyButtonAccept',
  // Termly
  '[data-testid="termly-banner"]',
  // Quantcast Choice
  '[class*="qc-cmp2"]',
  // Generic
  '[id*="cookie-banner"]',
  '[id*="cookie-consent"]',
  '[class*="cookie-banner"]',
  '[class*="cookie-consent"]',
];

const ACCEPT_LABELS = [
  /^accept all$/i,
  /^accept$/i,
  /^allow all$/i,
  /^i agree$/i,
  /^got it$/i,
  /^ok$/i,
  /^continue$/i,
  /^accept cookies$/i,
];

export function dismissCookieBanner(): boolean {
  for (const sel of COOKIE_BANNER_SELECTORS) {
    const banner = document.querySelector(sel);
    if (!banner) continue;
    if (!isElementVisible(banner)) continue;
    const accept = findAcceptButton(banner);
    if (accept) {
      (accept as HTMLElement).click();
      return true;
    }
  }
  // Generic scan: any fixed-position element near top/bottom of viewport
  // containing the word "cookie" with an accept-shaped button inside.
  const fixed = Array.from(document.querySelectorAll<HTMLElement>('[style*="fixed"], .fixed, .sticky'));
  for (const el of fixed) {
    if (!isElementVisible(el)) continue;
    const text = (el.innerText ?? '').toLowerCase();
    if (!/(cookie|gdpr|consent|tracking)/.test(text)) continue;
    const accept = findAcceptButton(el);
    if (accept) {
      (accept as HTMLElement).click();
      return true;
    }
  }
  return false;
}

function findAcceptButton(root: Element): Element | null {
  const buttons = root.querySelectorAll<HTMLElement>('button, [role="button"], a');
  for (const btn of Array.from(buttons)) {
    const label = (btn.innerText ?? btn.getAttribute('aria-label') ?? '').trim();
    if (ACCEPT_LABELS.some((re) => re.test(label))) return btn;
  }
  return null;
}

/**
 * Item 49 — onboarding tour auto-skip. Detects common product-tour
 * libraries by their DOM signatures and dismisses the active step.
 */
const TOUR_SIGNATURES: Array<{ name: string; selectors: string[] }> = [
  { name: 'Intro.js', selectors: ['.introjs-skipbutton', '.introjs-overlay'] },
  { name: 'Driver.js', selectors: ['.driver-popover-close-btn', '#driver-popover-item'] },
  { name: 'Shepherd', selectors: ['.shepherd-cancel-icon', '.shepherd-active'] },
  { name: 'Userflow', selectors: ['[data-userflow-id]', 'button[aria-label="Close tour"]'] },
  { name: 'Pendo', selectors: ['[id^="pendo-guide-"] [aria-label="Close"]'] },
  { name: 'WalkMe', selectors: ['.walkme-action-balloon-close-button', '.wm-close-button'] },
  { name: 'Appcues', selectors: ['[class*="appcues-skippable"] [aria-label="Skip"]'] },
];

export function dismissOnboardingTour(): { dismissed: boolean; library?: string } {
  for (const { name, selectors } of TOUR_SIGNATURES) {
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el && isElementVisible(el)) {
        // Click is safest dismissal — sending Escape misses some libs.
        const clickable =
          el.getAttribute('role') === 'button' || el.tagName === 'BUTTON' || el.tagName === 'A'
            ? el
            : el.querySelector('button, [role="button"]');
        (clickable as HTMLElement | null)?.click();
        return { dismissed: true, library: name };
      }
    }
  }
  return { dismissed: false };
}

/**
 * Item 47 — custom modal detection. Returns the deepest fixed/absolute
 * element that covers >40% of the viewport AND blocks pointer events on
 * the page beneath. Used by the action helper to decide whether a click
 * was actually swallowed by an unannounced modal.
 */
export function detectCustomModal(): Element | null {
  const all = Array.from(document.querySelectorAll<HTMLElement>('div, section, aside'));
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const minArea = vw * vh * 0.4;

  for (const el of all) {
    const styles = window.getComputedStyle(el);
    if (styles.position !== 'fixed' && styles.position !== 'absolute') continue;
    if (styles.display === 'none' || styles.visibility === 'hidden') continue;
    const rect = el.getBoundingClientRect();
    if (rect.width * rect.height < minArea) continue;
    const z = parseInt(styles.zIndex, 10);
    if (Number.isNaN(z) || z < 100) continue;
    // Looks like an overlay — confirm it has interactive children (so we
    // don't false-positive on full-bleed background gradients).
    if (!el.querySelector('button, [role="button"], input, a, [contenteditable]')) continue;
    return el;
  }
  return null;
}

/**
 * Item 51 — toast / live-region capture. Returns the textual content of
 * any [aria-live] / [role="status"] / [role="alert"] region that has been
 * updated since the last call.
 */
let lastLiveRegionText = new Map<Element, string>();

export function drainLiveRegions(): string[] {
  const regions = Array.from(
    document.querySelectorAll('[aria-live], [role="status"], [role="alert"]'),
  );
  const updates: string[] = [];
  const seen = new Set<Element>();
  for (const r of regions) {
    seen.add(r);
    const txt = ((r as HTMLElement).innerText ?? '').trim();
    if (!txt) continue;
    const prior = lastLiveRegionText.get(r);
    if (prior !== txt) {
      updates.push(txt);
      lastLiveRegionText.set(r, txt);
    }
  }
  // Garbage-collect entries for regions that disappeared from the DOM.
  for (const k of lastLiveRegionText.keys()) {
    if (!seen.has(k)) lastLiveRegionText.delete(k);
  }
  return updates;
}

/**
 * Item 53 — combobox vs select detection. Inputs that combine typing +
 * popup options need typeahead; pure selects need click-then-arrow. The
 * action helper switches strategies based on this classification.
 */
export type DropdownKind = 'native_select' | 'combobox' | 'listbox' | 'unknown';

export function classifyDropdown(el: Element): DropdownKind {
  const tag = el.tagName.toLowerCase();
  if (tag === 'select') return 'native_select';
  const role = (el.getAttribute('role') ?? '').toLowerCase();
  if (role === 'combobox') return 'combobox';
  if (role === 'listbox') return 'listbox';
  // ARIA pattern with aria-haspopup
  const hasPopup = el.getAttribute('aria-haspopup');
  if (hasPopup === 'listbox' || hasPopup === 'menu') return 'combobox';
  // Common "is a typeable filter" hint
  if (tag === 'input' && el.getAttribute('aria-autocomplete')) return 'combobox';
  return 'unknown';
}

function isElementVisible(el: Element): boolean {
  if (!(el instanceof HTMLElement) && !(el instanceof SVGElement)) return false;
  if ((el as HTMLElement).hidden) return false;
  if (el.getAttribute('aria-hidden') === 'true') return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}
